import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CONTRACT_FORM_WARNING,
  CONTRACT_KIND_LABELS,
  CONTRACT_KINDS,
  CONTRACT_PARTY_KINDS,
  CONTRACT_PARTY_LABELS,
  CONTRACT_PLACEHOLDERS,
  CONTRACT_TEMPLATES,
  type Contact,
  type Contract,
  type ContractKind,
  type ContractPartyKind,
  type Employee,
  type Paginated,
} from '@kontor/shared';
import { api, ApiError, queryString } from '../../api';
import { ContactPicker, type PickedContact } from '../../components/ContactPicker';
import './contracts.css';

/**
 * Maske für einen Vertrag, neu oder als Entwurf bearbeiten. Eine Vorlage liefert den Ausgangstext, die Platzhalter in doppelten geschweiften Klammern ersetzt der Server
 * beim Speichern. Ein gesendeter Vertrag lässt sich nicht mehr ändern, die Seite ist dann nur eine Ansicht.
 */
export function ContractFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const client = useQueryClient();

  const existing = useQuery({ queryKey: ['contract', id], queryFn: async () => (await api<{ data: Contract }>('GET', `/contracts/${id}`)).data, enabled: editing });
  const employees = useQuery({ queryKey: ['employees', { pick: 'contracts' }], queryFn: async () => (await api<Paginated<Employee>>('GET', `/employees${queryString({ perPage: 100 })}`)).data });

  const [template, setTemplate] = useState('');
  const [kind, setKind] = useState<ContractKind>('service');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [partyKind, setPartyKind] = useState<ContractPartyKind>('contact');
  const [employeeId, setEmployeeId] = useState('');
  const [contact, setContact] = useState<PickedContact | null>(null);
  const [partyName, setPartyName] = useState('');
  const [partyEmail, setPartyEmail] = useState('');
  const [validDays, setValidDays] = useState(14);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    const c = existing.data;
    if (!c) return;
    setKind(c.kind);
    setTitle(c.title);
    setBody(c.body);
    setPartyKind(c.partyKind);
    setEmployeeId(c.employeeId ?? '');
    setContact(c.contactId ? { id: c.contactId, name: c.partyName } : null);
    setPartyName(c.partyName);
    setPartyEmail(c.partyEmail);
    setValidDays(c.validDays);
  }, [existing.data]);

  const applyTemplate = (key: string) => {
    setTemplate(key);
    const t = CONTRACT_TEMPLATES.find((x) => x.key === key);
    if (!t) return;
    setKind(t.kind);
    setTitle(t.title);
    setBody(t.body);
  };

  const pickEmployee = (value: string) => {
    setEmployeeId(value);
    const e = employees.data?.find((x) => x.id === value);
    if (e) {
      setPartyName(`${e.firstName} ${e.lastName}`);
      setPartyEmail(e.email ?? '');
    }
  };
  const pickContact = async (c: PickedContact | null) => {
    setContact(c);
    if (!c) return;
    setPartyName(c.name);
    try {
      const full = (await api<{ data: Contact }>('GET', `/contacts/${c.id}`)).data;
      if (full.email) setPartyEmail(full.email);
    } catch {
      // Die E-Mail-Adresse lässt sich auch von Hand eintragen
    }
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        title,
        kind,
        body,
        partyKind,
        employeeId: partyKind === 'employee' && employeeId ? employeeId : null,
        contactId: partyKind === 'contact' && contact ? contact.id : null,
        partyName,
        partyEmail,
        validDays,
      };
      return editing ? api<{ data: Contract }>('PATCH', `/contracts/${id}`, payload) : api<{ data: Contract }>('POST', '/contracts', payload);
    },
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['contracts'] });
      client.setQueryData(['contract', res.data.id], res.data);
      navigate(`/vertraege/${res.data.id}`);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setFailure(null);
      } else setFailure(e instanceof ApiError ? e.message : 'Das Speichern hat nicht geklappt.');
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErrors({});
    setFailure(null);
    save.mutate();
  };

  if (editing && existing.isLoading) return <p className="muted">Wird geladen …</p>;
  if (editing && existing.data && existing.data.status !== 'draft') {
    return (
      <div className="card narrow">
        <h1>Vertrag nicht änderbar</h1>
        <p className="muted">Ein gesendeter Vertrag lässt sich nicht mehr ändern, damit die Unterschriften sich auf genau diesen Text beziehen. Ziehen Sie ihn zurück und legen Sie einen neuen an.</p>
        <Link className="btn btn--secondary" to={`/vertraege/${id}`}>Zurück zum Vertrag</Link>
      </div>
    );
  }

  const warning = CONTRACT_FORM_WARNING[kind];
  const field = (name: string) => errors[name];

  return (
    <>
      <p><Link to={editing ? `/vertraege/${id}` : '/vertraege'} className="back-link">← {editing ? 'Zurück zum Vertrag' : 'Alle Verträge'}</Link></p>
      <div className="page-head"><h1>{editing ? 'Vertrag bearbeiten' : 'Neuer Vertrag'}</h1></div>

      <form className="card contract-form" onSubmit={submit} noValidate>
        {failure && <div className="alert alert--error" role="alert">{failure}</div>}

        {!editing && (
          <label className="field">
            <span>Vorlage</span>
            <select className="input" value={template} onChange={(e) => applyTemplate(e.target.value)}>
              <option value="">Leer beginnen</option>
              {CONTRACT_TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.title}</option>)}
            </select>
            <span className="muted small">Die Vorlagen sind Mustertexte als Ausgangspunkt. Lassen Sie wichtige Verträge rechtlich prüfen.</span>
          </label>
        )}

        <div className="form-grid">
          <label className="field">
            <span>Art des Vertrags</span>
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value as ContractKind)}>
              {CONTRACT_KINDS.map((k) => <option key={k} value={k}>{CONTRACT_KIND_LABELS[k]}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Titel</span>
            <input className="input" value={title} maxLength={160} onChange={(e) => setTitle(e.target.value)} aria-invalid={!!field('title')} />
            {field('title') && <span className="field__error" role="alert">{field('title')}</span>}
          </label>
        </div>
        {warning && <div className="alert alert--warn" role="note">{warning}</div>}

        <fieldset className="field">
          <legend>Vertragspartner</legend>
          <div className="form-grid">
            <label className="field">
              <span>Wer unterschreibt?</span>
              <select className="input" value={partyKind} onChange={(e) => setPartyKind(e.target.value as ContractPartyKind)}>
                {CONTRACT_PARTY_KINDS.map((k) => <option key={k} value={k}>{CONTRACT_PARTY_LABELS[k]}</option>)}
              </select>
            </label>
            {partyKind === 'employee' && (
              <label className="field">
                <span>Mitarbeiter</span>
                <select className="input" value={employeeId} onChange={(e) => pickEmployee(e.target.value)}>
                  <option value="">Bitte wählen</option>
                  {employees.data?.map((e) => <option key={e.id} value={e.id}>{e.lastName}, {e.firstName}</option>)}
                </select>
              </label>
            )}
            {partyKind === 'contact' && (
              <div className="field">
                <span>Kontakt</span>
                <ContactPicker label="Kontakt" value={contact} onChange={(c) => void pickContact(c)} />
              </div>
            )}
          </div>
          <div className="form-grid">
            <label className="field">
              <span>Name im Vertrag</span>
              <input className="input" value={partyName} maxLength={160} onChange={(e) => setPartyName(e.target.value)} aria-invalid={!!field('partyName')} />
              {field('partyName') && <span className="field__error" role="alert">{field('partyName')}</span>}
            </label>
            <label className="field">
              <span>E-Mail für den Link und den Bestätigungscode</span>
              <input className="input" type="email" value={partyEmail} maxLength={200} onChange={(e) => setPartyEmail(e.target.value)} aria-invalid={!!field('partyEmail')} />
              {field('partyEmail') && <span className="field__error" role="alert">{field('partyEmail')}</span>}
              <span className="muted small">Der Code geht an diese Adresse. Wer sie lesen kann, kann unterschreiben. Prüfen Sie sie deshalb sorgfältig.</span>
            </label>
          </div>
        </fieldset>

        <label className="field">
          <span>Vertragstext</span>
          <textarea className="input" value={body} onChange={(e) => setBody(e.target.value)} aria-invalid={!!field('body')} />
          {field('body') && <span className="field__error" role="alert">{field('body')}</span>}
          <span className="muted small">
            Überschriften beginnen mit „§ 1“. Diese Platzhalter ersetzt Wirkia beim Speichern: {CONTRACT_PLACEHOLDERS.map((p) => `{{${p.key}}}`).join(', ')}. Angaben in eckigen Klammern wie „[Betrag]“ füllen Sie selbst aus, sonst lässt sich der Vertrag nicht senden.
          </span>
        </label>

        <label className="field">
          <span>Der Link zur Unterschrift gilt (Tage ab dem Senden)</span>
          <input className="input" type="number" min={1} max={90} value={validDays} onChange={(e) => setValidDays(Number(e.target.value))} style={{ maxWidth: '8rem' }} />
        </label>

        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Entwurf speichern'}</button>
          <Link className="btn btn--secondary" to={editing ? `/vertraege/${id}` : '/vertraege'}>Abbrechen</Link>
        </div>
      </form>
    </>
  );
}
